import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/api";

/**
 * Tavolata by IntegraNube
 * src/Pages/Mesero.js
 *
 * Contratos utilizados:
 *   GET    /restaurante/salon
 *   POST   /restaurante/pedidos
 *   GET    /inventario_productos
 *   GET    /inventario_productos/:productoId
 *   GET    /restaurante/pedidos/:id
 *   GET    /restaurante/mesas/:mesaId/pedido
 *   POST   /restaurante/pedidos/:pedidoId/items
 *   PATCH  /restaurante/pedidos/:pedidoId/items/:itemId
 *   DELETE /restaurante/pedidos/:pedidoId/items/:itemId
 *   POST   /restaurante/pedidos/:pedidoId/enviar
 *   POST   /restaurante/pedidos/:pedidoId/solicitar-cuenta
 *
 * Destacados:
 *   GET /inventario_productos/destacados?limit=30
 *
 * La categoría "⭐ Destacados" es virtual y proviene de
 * /inventario_productos/destacados.
 *
 * El catálogo normal usa paginación nativa de /inventario_productos:
 * page, limit, search, cat_id y estado.
 *
 * /mesero funciona sin parámetros:
 *   selector de zona/mesa -> abre o continúa pedido -> toma orden.
 *
 * /mesero?mesaId=...&pedidoId=... sigue funcionando como acceso directo.
 */

const DESTACADOS_ID = "__DESTACADOS__";

function first(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
}

function num(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function bool(value, fallback = false) {
  if (value === undefined || value === null) return fallback;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;

  const normalized = String(value).trim().toLowerCase();
  if (["true", "1", "si", "sí", "s", "yes"].includes(normalized)) return true;
  if (["false", "0", "no", "n"].includes(normalized)) return false;

  return fallback;
}

function money(value) {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(num(value, 0));
}

function errorMessage(error, fallback = "No fue posible completar la operación.") {
  return (
    error?.friendlyMessage ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function extractArray(payload, keys = []) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  for (const key of keys) {
    if (Array.isArray(payload[key])) return payload[key];
  }

  if (payload.data) {
    const nested = extractArray(payload.data, keys);
    if (nested.length) return nested;
  }

  if (payload.result) {
    const nested = extractArray(payload.result, keys);
    if (nested.length) return nested;
  }

  return [];
}

function getRestaurantConfig(raw) {
  return (
    raw?.restaurant ||
    raw?.restaurante ||
    raw?.configuracion_restaurant ||
    raw?.configuracion_restaurante ||
    {}
  );
}

function normalizeCategories(raw) {
  const restaurant = getRestaurantConfig(raw);

  const candidates = [
    raw?.categorias,
    raw?.categories,
    restaurant?.categorias,
    restaurant?.categories,
  ];

  const out = [];

  for (const candidate of candidates) {
    if (!Array.isArray(candidate)) continue;

    for (const category of candidate) {
      if (category == null) continue;

      if (typeof category === "object") {
        const id = first(
          category.id,
          category.categoria_id,
          category.category_id,
          category.slug,
          category.codigo,
          category.nombre
        );
        const nombre = first(
          category.nombre,
          category.name,
          category.categoria_nombre,
          category.descripcion,
          id
        );

        if (id != null && nombre != null) {
          out.push({
            id: String(id),
            nombre: String(nombre),
          });
        }
      } else {
        out.push({
          id: String(category),
          nombre: String(category),
        });
      }
    }
  }

  const single =
    raw?.categoria && typeof raw.categoria === "object"
      ? raw.categoria
      : restaurant?.categoria && typeof restaurant.categoria === "object"
        ? restaurant.categoria
        : null;

  if (single) {
    const id = first(
      single.id,
      single.categoria_id,
      single.category_id,
      single.slug,
      single.codigo,
      single.nombre
    );
    const nombre = first(
      single.nombre,
      single.name,
      single.categoria_nombre,
      id
    );

    if (id != null && nombre != null) {
      out.push({
        id: String(id),
        nombre: String(nombre),
      });
    }
  }

  const idCategoria = first(
    raw?.categoria_id,
    raw?.id_categoria,
    raw?.category_id,
    restaurant?.categoria_id,
    restaurant?.id_categoria
  );

  const nombreCategoria = first(
    raw?.categoria_nombre,
    raw?.category_name,
    restaurant?.categoria_nombre
  );

  if (idCategoria != null || nombreCategoria != null) {
    out.push({
      id: String(first(idCategoria, nombreCategoria)),
      nombre: String(first(nombreCategoria, idCategoria, "Sin categoría")),
    });
  }

  const unique = new Map();
  out.forEach((category) => {
    if (!unique.has(category.id)) unique.set(category.id, category);
  });

  return Array.from(unique.values());
}

function normalizeGroups(raw) {
  const restaurant = getRestaurantConfig(raw);

  const groups = first(
    restaurant?.grupos_modificadores,
    restaurant?.modifier_groups,
    raw?.grupos_modificadores,
    raw?.modifier_groups,
    []
  );

  if (!Array.isArray(groups)) return [];

  return groups
    .map((group, index) => {
      const id = String(
        first(
          group?.id,
          group?.grupo_id,
          group?.codigo,
          group?.slug,
          `grupo-${index + 1}`
        )
      );

      const tipoRaw = String(
        first(group?.tipo, group?.type, group?.modo, "multiple")
      ).toLowerCase();

      const tipo = ["single", "multiple", "quantity"].includes(tipoRaw)
        ? tipoRaw
        : tipoRaw === "unico" || tipoRaw === "único"
          ? "single"
          : tipoRaw === "cantidad"
            ? "quantity"
            : "multiple";

      const rawOptions = first(group?.opciones, group?.options, []);
      const options = Array.isArray(rawOptions)
        ? rawOptions.map((option, optionIndex) => ({
            id: String(
              first(
                option?.id,
                option?.opcion_id,
                option?.codigo,
                option?.slug,
                `${id}-opcion-${optionIndex + 1}`
              )
            ),
            nombre: String(
              first(
                option?.nombre,
                option?.name,
                option?.descripcion,
                option?.label,
                `Opción ${optionIndex + 1}`
              )
            ),
            precioAdicional: num(
              first(
                option?.precio_adicional,
                option?.precio,
                option?.recargo,
                option?.additional_price
              ),
              0
            ),
            maxCantidad: Math.max(
              1,
              num(first(option?.max_cantidad, option?.max_quantity), 1)
            ),
            productoExtraId: first(
              option?.producto_extra_id,
              option?.extra_product_id
            ),
            raw: option,
          }))
        : [];

      return {
        id,
        nombre: String(
          first(group?.nombre, group?.name, group?.titulo, `Opciones ${index + 1}`)
        ),
        tipo,
        obligatorio: bool(
          first(group?.obligatorio, group?.required, group?.requerido),
          false
        ),
        minimo: Math.max(
          0,
          num(first(group?.minimo, group?.min, group?.min_selecciones), 0)
        ),
        maximo: Math.max(
          0,
          num(first(group?.maximo, group?.max, group?.max_selecciones), 0)
        ),
        opciones: options,
        raw: group,
      };
    })
    .filter((group) => group.opciones.length > 0);
}

function normalizeProduct(raw, destacadoMeta = null) {
  const restaurant = getRestaurantConfig(raw);

  return {
    id: String(first(raw?.id, raw?.producto_id, raw?.product_id, raw?.uuid)),
    sku: String(first(raw?.sku, raw?.codigo, raw?.codigo_barras, "") || ""),
    nombre: String(
      first(raw?.nombre, raw?.name, raw?.producto_nombre, "Producto")
    ),
    descripcion: String(
      first(raw?.descripcion, raw?.description, restaurant?.descripcion, "") || ""
    ),
    precio: num(
      first(
        raw?.precio_bruto,
        raw?.precio,
        raw?.precio_venta,
        raw?.precio_unitario,
        raw?.precio_neto
      ),
      0
    ),
    imagen: first(
      raw?.imagen_publica,
      raw?.primera_imagen_publica,
      raw?.imagen_principal?.url_publica,
      raw?.imagen,
      raw?.image,
      raw?.ruta_imagen,
      raw?.primera_imagen,
      destacadoMeta?.imagen_publica
    ),
    categorias: normalizeCategories(raw),
    grupos: normalizeGroups(raw),
    permiteObservacion: bool(
      first(
        restaurant?.permite_observacion,
        raw?.permite_observacion,
        restaurant?.allow_observation
      ),
      true
    ),
    esComestible: bool(
      first(
        restaurant?.es_comestible,
        raw?.es_comestible,
        raw?.tipo_item === "PRODUCTO"
      ),
      true
    ),
    activo: bool(first(raw?.activo, raw?.disponible, true), true),
    raw,
  };
}

function extractProducts(payload) {
  return extractArray(payload, [
    "productos",
    "products",
    "items",
    "rows",
    "catalogo",
  ]);
}

function normalizePedido(payload) {
  if (!payload) return null;

  const root =
    payload?.data && typeof payload.data === "object" && !Array.isArray(payload.data)
      ? payload.data
      : payload;

  const pedido =
    root?.pedido ||
    root?.pedido_activo ||
    root?.pedidoActual ||
    root?.pedido_actual ||
    root;

  if (!pedido || typeof pedido !== "object" || Array.isArray(pedido)) return null;

  const id = first(pedido?.id, pedido?.pedido_id, pedido?.pedidoId);
  if (!id) return null;

  const items = extractArray(pedido, [
    "items",
    "detalle",
    "lineas",
    "productos",
    "pedido_items",
  ]);

  return {
    ...pedido,
    id: String(id),
    items,
  };
}

function itemProductId(item) {
  return String(
    first(
      item?.producto_id,
      item?.product_id,
      item?.producto?.id,
      item?.productoId,
      ""
    )
  );
}

function itemId(item) {
  return String(first(item?.id, item?.item_id, item?.pedido_item_id, ""));
}

function itemName(item) {
  return String(
    first(
      item?.nombre_snapshot,
      item?.producto_nombre,
      item?.nombre,
      item?.producto?.nombre,
      "Producto"
    )
  );
}

function itemQty(item) {
  return Math.max(1, num(first(item?.cantidad, item?.qty, item?.quantity), 1));
}

function itemUnitPrice(item) {
  return num(
    first(
      item?.precio_unitario,
      item?.precio,
      item?.precio_bruto,
      item?.producto?.precio_bruto,
      item?.producto?.precio,
      item?.producto?.precio_neto
    ),
    0
  );
}

function itemTotal(item) {
  const explicit = first(
    item?.total,
    item?.subtotal,
    item?.total_linea,
    item?.monto_total
  );
  return explicit !== null ? num(explicit, 0) : itemUnitPrice(item) * itemQty(item);
}

function itemModifiers(item) {
  return extractArray(item, [
    "modificadores",
    "modifiers",
    "opciones",
    "item_modificadores",
  ]);
}

function selectedFromItem(product, item) {
  const selected = {};

  for (const group of product.grupos) {
    selected[group.id] = {};
  }

  for (const modifier of itemModifiers(item)) {
    const optionId = String(
      first(
        modifier?.opcion_id,
        modifier?.id_opcion,
        modifier?.opcion?.id,
        modifier?.id,
        ""
      )
    );

    if (!optionId) continue;

    for (const group of product.grupos) {
      const option = group.opciones.find(
        (candidate) => String(candidate.id) === optionId
      );

      if (!option) continue;

      selected[group.id] ||= {};
      selected[group.id][optionId] =
        (selected[group.id][optionId] || 0) +
        Math.max(1, num(first(modifier?.cantidad, modifier?.quantity), 1));
      break;
    }
  }

  return selected;
}

function modifierPayload(product, selected) {
  const payload = [];

  for (const group of product.grupos) {
    const groupSelected = selected[group.id] || {};

    for (const option of group.opciones) {
      const qty = Math.max(0, num(groupSelected[option.id], 0));

      // Contrato confirmado del backend: [{ opcion_id }]
      // Para quantity repetimos la opción según la cantidad.
      for (let i = 0; i < qty; i += 1) {
        payload.push({ opcion_id: option.id });
      }
    }
  }

  return payload;
}

function modifierExtra(product, selected) {
  let extra = 0;

  for (const group of product.grupos) {
    const groupSelected = selected[group.id] || {};

    for (const option of group.opciones) {
      const qty = Math.max(0, num(groupSelected[option.id], 0));
      extra += qty * num(option.precioAdicional, 0);
    }
  }

  return extra;
}

function selectedCount(group, selected) {
  const values = selected[group.id] || {};
  return Object.values(values).reduce((sum, quantity) => {
    return sum + (num(quantity, 0) > 0 ? 1 : 0);
  }, 0);
}

function hasValidModifierSelection(product, selected) {
  for (const group of product.grupos) {
    const count = selectedCount(group, selected);

    if (group.obligatorio && count === 0) return false;
    if (group.minimo > 0 && count < group.minimo) return false;
    if (group.maximo > 0 && count > group.maximo) return false;
  }

  return true;
}



function normalizeWaiterSalon(payload) {
  const rawZones = Array.isArray(payload?.zonas)
    ? payload.zonas
    : Array.isArray(payload?.data?.zonas)
      ? payload.data.zonas
      : [];

  const zonas = rawZones
    .map((zone, index) => ({
      id: String(first(zone?.id, zone?.zona_id, index + 1)),
      nombre: String(first(zone?.nombre, zone?.zona_nombre, `Zona ${index + 1}`)),
      descripcion: String(first(zone?.descripcion, "") || ""),
      orden: num(first(zone?.orden, index + 1), index + 1),
    }))
    .sort((a, b) => a.orden - b.orden || a.nombre.localeCompare(b.nombre, "es"));

  const mesas = rawZones.flatMap((zone) => {
    const zonaId = String(first(zone?.id, zone?.zona_id, ""));
    const zonaNombre = String(first(zone?.nombre, zone?.zona_nombre, "Zona"));
    const nested = Array.isArray(zone?.mesas) ? zone.mesas : [];

    return nested.map((mesa, index) => ({
      id: String(first(mesa?.id, mesa?.mesa_id, `${zonaId}-${index + 1}`)),
      zonaId: String(first(mesa?.zona_id, zonaId)),
      zonaNombre,
      codigo: String(first(mesa?.codigo, "") || ""),
      nombre: String(first(mesa?.nombre, mesa?.mesa_nombre, `Mesa ${index + 1}`)),
      capacidad: Math.max(1, num(first(mesa?.capacidad, 1), 1)),
      pedidoId: first(mesa?.pedido_id, mesa?.pedidoId)
        ? String(first(mesa?.pedido_id, mesa?.pedidoId))
        : null,
      pedidoNumero: first(mesa?.pedido_numero, mesa?.pedidoNumero),
      pedidoEstado: String(first(mesa?.pedido_estado, "") || "").toUpperCase(),
      meseroUsuarioId: first(mesa?.mesero_usuario_id, mesa?.meseroUsuarioId),
      comensales: Math.max(0, num(first(mesa?.comensales, 0), 0)),
      total: num(first(mesa?.total, 0), 0),
      abiertoAt: first(mesa?.abierto_at, mesa?.abiertoAt),
      estado: String(first(mesa?.estado, "LIBRE") || "LIBRE").toUpperCase(),
      minutos: Math.max(0, num(first(mesa?.minutos_ocupada, 0), 0)),
      itemsPendientes: Math.max(0, num(first(mesa?.items_pendientes, 0), 0)),
      itemsListos: Math.max(0, num(first(mesa?.items_listos, 0), 0)),
    }));
  });

  return { zonas, mesas };
}

function waiterMesaState(mesa) {
  if (!mesa) return "LIBRE";
  if (mesa.itemsListos > 0) return "LISTO";

  const raw = String(first(mesa.pedidoEstado, mesa.estado, "") || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

  if (!mesa.pedidoId) {
    if (raw.includes("RESERV")) return "RESERVADA";
    if (raw.includes("FUERA") || raw.includes("BLOQUE")) return "FUERA_SERVICIO";
    return "LIBRE";
  }

  if (raw.includes("CUENTA")) return "CUENTA_SOLICITADA";
  if (raw.includes("COCINA") || raw.includes("PREPAR")) return "EN_COCINA";
  if (mesa.itemsPendientes > 0) return "EN_COCINA";
  if (raw.includes("PARCIAL")) return "OCUPADA";
  if (raw.includes("ABIER")) return "ABIERTA";
  if (raw.includes("OCUP")) return "OCUPADA";

  return "OCUPADA";
}

function waiterMesaMeta(mesa) {
  const state = waiterMesaState(mesa);

  const map = {
    LIBRE: {
      label: "Libre",
      className: "libre",
      icon: "✓",
    },
    RESERVADA: {
      label: "Reservada",
      className: "reservada",
      icon: "R",
    },
    ABIERTA: {
      label: "Abierta",
      className: "abierta",
      icon: "●",
    },
    OCUPADA: {
      label: "Ocupada",
      className: "ocupada",
      icon: "●",
    },
    EN_COCINA: {
      label: "En cocina",
      className: "cocina",
      icon: "C",
    },
    LISTO: {
      label: "Listo",
      className: "listo",
      icon: "!",
    },
    CUENTA_SOLICITADA: {
      label: "Cuenta solicitada",
      className: "cuenta",
      icon: "$",
    },
    FUERA_SERVICIO: {
      label: "Fuera de servicio",
      className: "fuera",
      icon: "×",
    },
  };

  return map[state] || map.OCUPADA;
}

function syncMeseroUrl(mesaId, pedidoId) {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete("mesa_id");
    url.searchParams.delete("pedido_id");

    if (mesaId) url.searchParams.set("mesaId", String(mesaId));
    else url.searchParams.delete("mesaId");

    if (pedidoId) url.searchParams.set("pedidoId", String(pedidoId));
    else url.searchParams.delete("pedidoId");

    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  } catch (_) {
    // Navegación opcional; no afecta la operación si el navegador la bloquea.
  }
}

export default function Mesero() {
  const query = useMemo(() => new URLSearchParams(window.location.search), []);
  const initialMesaId = query.get("mesaId") || query.get("mesa_id") || "";
  const initialPedidoId = query.get("pedidoId") || query.get("pedido_id") || "";

  const [vista, setVista] = useState(
    initialMesaId || initialPedidoId ? "pedido" : "mesas"
  );
  const [mesaIdActivo, setMesaIdActivo] = useState(initialMesaId);
  const [pedidoIdActivo, setPedidoIdActivo] = useState(initialPedidoId);

  const [zonasSalon, setZonasSalon] = useState([]);
  const [mesasSalon, setMesasSalon] = useState([]);
  const [zonaSalonActiva, setZonaSalonActiva] = useState("TODAS");
  const [filtroMesas, setFiltroMesas] = useState("TODAS");
  const [loadingSalon, setLoadingSalon] = useState(true);
  const [refrescandoSalon, setRefrescandoSalon] = useState(false);

  const [mesaEnApertura, setMesaEnApertura] = useState(null);
  const [personasApertura, setPersonasApertura] = useState(1);

  const [productos, setProductos] = useState([]);
  const [destacadosMeta, setDestacadosMeta] = useState([]);
  const [categoriasInventario, setCategoriasInventario] = useState([]);
  const [categoriaActiva, setCategoriaActiva] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [busquedaDebounced, setBusquedaDebounced] = useState("");

  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalProductos, setTotalProductos] = useState(0);
  const productosPorPagina = 24;
  const [catalogoBaseListo, setCatalogoBaseListo] = useState(false);

  const [pedido, setPedido] = useState(null);
  const [loadingCatalogo, setLoadingCatalogo] = useState(true);
  const [loadingPedido, setLoadingPedido] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);

  const [notice, setNotice] = useState(null);
  const [pedidoAbiertoMobile, setPedidoAbiertoMobile] = useState(false);
  const [cuentaModalOpen, setCuentaModalOpen] = useState(false);

  const [editor, setEditor] = useState(null);
  const [editorLoading, setEditorLoading] = useState(false);

  const showNotice = useCallback((type, message) => {
    setNotice({ type, message });
    window.clearTimeout(showNotice._timer);
    showNotice._timer = window.setTimeout(() => setNotice(null), 3800);
  }, []);


  const loadSalon = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefrescandoSalon(true);
    else setLoadingSalon(true);

    try {
      const response = await api.get("/restaurante/salon");
      const normalized = normalizeWaiterSalon(response.data);

      setZonasSalon(normalized.zonas);
      setMesasSalon(normalized.mesas);

      setZonaSalonActiva((current) => {
        if (current === "TODAS") return current;
        if (normalized.zonas.some((zona) => zona.id === current)) return current;
        return "TODAS";
      });
    } catch (error) {
      showNotice(
        "error",
        errorMessage(error, "No fue posible cargar las mesas del restaurante.")
      );
    } finally {
      setLoadingSalon(false);
      setRefrescandoSalon(false);
    }
  }, [showNotice]);

  const enterPedido = useCallback((mesaId, pedidoId) => {
    const nextMesaId = mesaId ? String(mesaId) : "";
    const nextPedidoId = pedidoId ? String(pedidoId) : "";

    setMesaIdActivo(nextMesaId);
    setPedidoIdActivo(nextPedidoId);
    setPedidoAbiertoMobile(false);
    setVista("pedido");
    syncMeseroUrl(nextMesaId, nextPedidoId);
  }, []);

  const volverAMesas = useCallback(() => {
    setVista("mesas");
    setPedidoAbiertoMobile(false);
    setEditor(null);
    setPedido(null);
    setMesaIdActivo("");
    setPedidoIdActivo("");
    syncMeseroUrl("", "");
  }, []);

  const seleccionarMesa = useCallback((mesa) => {
    if (!mesa) return;

    const state = waiterMesaState(mesa);

    if (state === "FUERA_SERVICIO") {
      showNotice("error", `${mesa.nombre} está fuera de servicio.`);
      return;
    }

    if (mesa.pedidoId) {
      enterPedido(mesa.id, mesa.pedidoId);
      return;
    }

    setMesaEnApertura(mesa);
    setPersonasApertura(Math.max(1, Math.min(mesa.capacidad || 1, 2)));
  }, [enterPedido, showNotice]);

  const abrirMesa = useCallback(async () => {
    if (!mesaEnApertura?.id) return;

    const comensales = Math.max(
      1,
      Math.min(99, Math.round(num(personasApertura, 1)))
    );

    setLoadingAction(true);

    try {
      const response = await api.post("/restaurante/pedidos", {
        tipo: "MESA",
        mesa_id: mesaEnApertura.id,
        comensales,
      });

      const created = normalizePedido(response.data);
      const pedidoId = first(created?.id, response?.data?.id);

      if (!pedidoId) {
        throw new Error("El backend creó la mesa, pero no devolvió el ID del pedido.");
      }

      const mesaId = mesaEnApertura.id;
      const mesaNombre = mesaEnApertura.nombre;

      setMesaEnApertura(null);
      setPedido(created);
      enterPedido(mesaId, pedidoId);

      showNotice(
        "success",
        `${mesaNombre} abierta para ${comensales} persona${comensales === 1 ? "" : "s"}.`
      );

      loadSalon({ silent: true });
    } catch (error) {
      const code = error?.response?.data?.code;

      if (
        code === "MESA_CON_PEDIDO_ACTIVO" ||
        code === "PEDIDO_ACTIVO_EXISTENTE"
      ) {
        await loadSalon({ silent: false });
        setMesaEnApertura(null);
        showNotice(
          "error",
          "La mesa acaba de ser abierta por otro usuario. Actualizamos su estado."
        );
      } else {
        showNotice(
          "error",
          errorMessage(error, "No fue posible abrir la mesa.")
        );
      }
    } finally {
      setLoadingAction(false);
    }
  }, [
    mesaEnApertura,
    personasApertura,
    enterPedido,
    loadSalon,
    showNotice,
  ]);

  const loadCatalog = useCallback(async () => {
    try {
      const [filtrosResult, destacadosResult] = await Promise.allSettled([
        api.get("/inventario_productos/filtros"),
        api.get("/inventario_productos/destacados?limit=30"),
      ]);

      const categoriasRaw =
        filtrosResult.status === "fulfilled" &&
        Array.isArray(filtrosResult.value?.data?.categorias)
          ? filtrosResult.value.data.categorias
          : [];

      const categoriasNormalizadas = categoriasRaw
        .filter((category) => category?.id != null)
        .map((category) => ({
          id: String(category.id),
          nombre: String(first(category.nombre, category.name, `Categoría ${category.id}`)),
        }));

      const rawFeatured =
        destacadosResult.status === "fulfilled"
          ? extractArray(destacadosResult.value.data, [
              "productos",
              "products",
              "items",
            ])
          : [];

      setCategoriasInventario(categoriasNormalizadas);
      setDestacadosMeta(rawFeatured);

      setCategoriaActiva((current) => {
        if (current) return current;
        if (rawFeatured.length) return DESTACADOS_ID;
        return categoriasNormalizadas[0]?.id || "";
      });

      setCatalogoBaseListo(true);
    } catch (error) {
      showNotice(
        "error",
        errorMessage(error, "No fue posible cargar las categorías del inventario.")
      );
      setCatalogoBaseListo(true);
    }
  }, [showNotice]);


  const loadProductosPaginados = useCallback(async () => {
    if (vista !== "pedido" || !catalogoBaseListo) return;

    setLoadingCatalogo(true);

    try {
      // Con búsqueda hacemos búsqueda global, independiente de la pestaña activa.
      if (busquedaDebounced) {
        const response = await api.get("/inventario_productos", {
          params: {
            page: pagina,
            limit: productosPorPagina,
            search: busquedaDebounced,
            estado: "disponible",
          },
        });

        const data = response?.data || {};
        const rows = extractProducts(data);

        setProductos(
          rows
            .map((raw) => normalizeProduct(raw))
            .filter((product) => product.id && product.activo !== false)
        );
        setTotalProductos(num(data?.totalProductos, rows.length));
        setTotalPaginas(Math.max(1, num(data?.totalPaginas, 1)));
        return;
      }

      // Destacados es una categoría virtual y proviene de su endpoint específico.
      if (categoriaActiva === DESTACADOS_ID) {
        const normalized = destacadosMeta
          .map((raw) => normalizeProduct(raw, raw))
          .filter((product) => product.id && product.activo !== false);

        setProductos(normalized);
        setTotalProductos(normalized.length);
        setTotalPaginas(1);
        return;
      }

      const params = {
        page: pagina,
        limit: productosPorPagina,
        estado: "disponible",
      };

      if (categoriaActiva) {
        params.cat_id = categoriaActiva;
      }

      const response = await api.get("/inventario_productos", { params });
      const data = response?.data || {};
      const rows = extractProducts(data);

      setProductos(
        rows
          .map((raw) => normalizeProduct(raw))
          .filter((product) => product.id && product.activo !== false)
      );
      setTotalProductos(num(data?.totalProductos, rows.length));
      setTotalPaginas(Math.max(1, num(data?.totalPaginas, 1)));

      console.debug("[Tavolata Mesero] página inventario", {
        pagina,
        totalPaginas: Math.max(1, num(data?.totalPaginas, 1)),
        totalProductos: num(data?.totalProductos, rows.length),
        categoria: categoriaActiva || null,
      });
    } catch (error) {
      setProductos([]);
      setTotalProductos(0);
      setTotalPaginas(1);
      showNotice(
        "error",
        errorMessage(error, "No fue posible cargar los productos disponibles.")
      );
    } finally {
      setLoadingCatalogo(false);
    }
  }, [
    vista,
    catalogoBaseListo,
    categoriaActiva,
    busquedaDebounced,
    pagina,
    destacadosMeta,
    showNotice,
  ]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setBusquedaDebounced(busqueda.trim());
      setPagina(1);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [busqueda]);

  useEffect(() => {
    setPagina(1);
  }, [categoriaActiva]);

  const loadPedido = useCallback(async (context = {}) => {
    const targetPedidoId =
      context?.pedidoId !== undefined ? context.pedidoId : pedidoIdActivo;
    const targetMesaId =
      context?.mesaId !== undefined ? context.mesaId : mesaIdActivo;

    setLoadingPedido(true);

    try {
      let response;

      if (targetPedidoId) {
        response = await api.get(`/restaurante/pedidos/${targetPedidoId}`);
      } else if (targetMesaId) {
        response = await api.get(`/restaurante/mesas/${targetMesaId}/pedido`);
      } else {
        setPedido(null);
        return null;
      }

      const normalized = normalizePedido(response.data);
      setPedido(normalized);

      if (normalized?.id) {
        const resolvedMesaId = first(normalized?.mesa_id, targetMesaId, "");
        setMesaIdActivo(resolvedMesaId ? String(resolvedMesaId) : "");
        setPedidoIdActivo(String(normalized.id));
        syncMeseroUrl(resolvedMesaId, normalized.id);
        return normalized;
      }

      // Si se abrió /mesero?mesaId=... pero la mesa no tiene pedido,
      // volvemos al selector operacional en vez de dejar una carta sin pedido.
      if (targetMesaId) {
        setVista("mesas");
        setMesaIdActivo("");
        setPedidoIdActivo("");
        syncMeseroUrl("", "");
      }

      return null;
    } catch (error) {
      const status = error?.response?.status;
      const code = error?.response?.data?.code;

      if (
        status === 404 ||
        status === 204 ||
        code === "PEDIDO_ACTIVO_NO_ENCONTRADO"
      ) {
        setPedido(null);
        return null;
      }

      showNotice(
        "error",
        errorMessage(error, "No fue posible cargar el pedido de la mesa.")
      );
      return null;
    } finally {
      setLoadingPedido(false);
    }
  }, [mesaIdActivo, pedidoIdActivo, showNotice]);

  useEffect(() => {
    return () => window.clearTimeout(showNotice._timer);
  }, [showNotice]);

  useEffect(() => {
    if (vista !== "mesas") return undefined;

    loadSalon({ silent: false });

    const timer = window.setInterval(() => {
      loadSalon({ silent: true });
    }, 10000);

    return () => window.clearInterval(timer);
  }, [vista, loadSalon]);

  useEffect(() => {
    if (vista !== "pedido") return;

    if (!catalogoBaseListo) loadCatalog();
    loadPedido();
  }, [
    vista,
    mesaIdActivo,
    pedidoIdActivo,
    catalogoBaseListo,
    loadCatalog,
    loadPedido,
  ]);

  useEffect(() => {
    loadProductosPaginados();
  }, [loadProductosPaginados]);


  const mesasVisibles = useMemo(() => {
    let rows = mesasSalon;

    if (zonaSalonActiva !== "TODAS") {
      rows = rows.filter((mesa) => mesa.zonaId === zonaSalonActiva);
    }

    if (filtroMesas === "LIBRES") {
      rows = rows.filter((mesa) => waiterMesaState(mesa) === "LIBRE");
    } else if (filtroMesas === "ACTIVAS") {
      rows = rows.filter((mesa) => Boolean(mesa.pedidoId));
    } else if (filtroMesas === "LISTAS") {
      rows = rows.filter((mesa) => mesa.itemsListos > 0);
    } else if (filtroMesas === "CUENTA") {
      rows = rows.filter(
        (mesa) => waiterMesaState(mesa) === "CUENTA_SOLICITADA"
      );
    }

    return [...rows].sort((a, b) => {
      const stateA = waiterMesaState(a);
      const stateB = waiterMesaState(b);

      const priority = {
        LISTO: 0,
        CUENTA_SOLICITADA: 1,
        EN_COCINA: 2,
        ABIERTA: 3,
        OCUPADA: 4,
        LIBRE: 5,
        RESERVADA: 6,
        FUERA_SERVICIO: 7,
      };

      const stateDiff = (priority[stateA] ?? 99) - (priority[stateB] ?? 99);
      if (stateDiff) return stateDiff;

      return a.nombre.localeCompare(b.nombre, "es", { numeric: true });
    });
  }, [mesasSalon, zonaSalonActiva, filtroMesas]);

  const metricasMesas = useMemo(() => {
    return {
      total: mesasSalon.length,
      libres: mesasSalon.filter((mesa) => waiterMesaState(mesa) === "LIBRE").length,
      activas: mesasSalon.filter((mesa) => Boolean(mesa.pedidoId)).length,
      listas: mesasSalon.filter((mesa) => mesa.itemsListos > 0).length,
      cuenta: mesasSalon.filter(
        (mesa) => waiterMesaState(mesa) === "CUENTA_SOLICITADA"
      ).length,
    };
  }, [mesasSalon]);

  const productosById = useMemo(() => {
    return new Map(productos.map((product) => [String(product.id), product]));
  }, [productos]);

  const categorias = useMemo(() => {
    return [
      ...(destacadosMeta.length
        ? [
            {
              id: DESTACADOS_ID,
              nombre: "⭐ Destacados",
              virtual: true,
            },
          ]
        : []),
      ...categoriasInventario,
    ];
  }, [categoriasInventario, destacadosMeta]);

  useEffect(() => {
    if (!catalogoBaseListo || !categorias.length) return;

    if (
      categoriaActiva &&
      categorias.some((category) => category.id === categoriaActiva)
    ) {
      return;
    }

    setCategoriaActiva(categorias[0].id);
  }, [catalogoBaseListo, categorias, categoriaActiva]);

  // El filtrado normal se realiza en PostgreSQL usando page/search/cat_id.
  // Para Destacados, `productos` ya contiene exactamente el endpoint destacado.
  const productosVisibles = productos;

  const pedidoItems = useMemo(() => {
    return Array.isArray(pedido?.items) ? pedido.items : [];
  }, [pedido]);

  const pedidoTotal = useMemo(() => {
    const explicit = first(
      pedido?.total,
      pedido?.total_bruto,
      pedido?.monto_total
    );

    if (explicit !== null) return num(explicit, 0);

    return pedidoItems.reduce((sum, item) => sum + itemTotal(item), 0);
  }, [pedido, pedidoItems]);

  const cantidadItems = useMemo(() => {
    return pedidoItems.reduce((sum, item) => sum + itemQty(item), 0);
  }, [pedidoItems]);

  const itemsBorrador = useMemo(
    () =>
      pedidoItems.filter(
        (item) => String(item?.estado || "").toUpperCase() === "BORRADOR"
      ),
    [pedidoItems]
  );

  const itemsEnCurso = useMemo(() => {
    const estadosEnCurso = new Set([
      "ENVIADO",
      "PENDIENTE",
      "RECIBIDO",
      "RECIBIDA",
      "PREPARACION",
      "EN_PREPARACION",
      "LISTO",
    ]);

    return pedidoItems.filter((item) =>
      estadosEnCurso.has(String(item?.estado || "").toUpperCase())
    );
  }, [pedidoItems]);

  const pedidoEstado = String(pedido?.estado || "").toUpperCase();
  const cuentaSolicitada =
    pedidoEstado === "CUENTA_SOLICITADA" ||
    pedidoEstado === "PARCIALMENTE_PAGADO";

  const puedeSolicitarCuenta =
    Boolean(pedido?.id) &&
    pedidoEstado === "ABIERTO" &&
    itemsBorrador.length === 0 &&
    itemsEnCurso.length === 0 &&
    pedidoItems.some(
      (item) => String(item?.estado || "").toUpperCase() !== "CANCELADO"
    );

  async function getProductDetail(product) {
    setEditorLoading(true);

    try {
      const response = await api.get(`/inventario_productos/${product.id}`);
      const raw =
        response?.data?.producto ||
        response?.data?.data ||
        response?.data;

      if (!raw || typeof raw !== "object") return product;

      return normalizeProduct(raw, {
        imagen_publica: product.imagen,
      });
    } catch {
      return product;
    } finally {
      setEditorLoading(false);
    }
  }

  async function openProduct(product, item = null) {
    if (!pedido?.id) {
      showNotice(
        "error",
        "Selecciona o abre una mesa antes de agregar productos."
      );
      return;
    }

    if (cuentaSolicitada) {
      showNotice(
        "error",
        "La cuenta ya fue solicitada. El pedido quedó listo para Caja."
      );
      return;
    }

    const detail = await getProductDetail(product);

    const requiresEditor =
      detail.grupos.length > 0 ||
      detail.permiteObservacion ||
      Boolean(item);

    if (!requiresEditor && !item) {
      await addSimpleProduct(detail);
      return;
    }

    setEditor({
      mode: item ? "edit" : "add",
      item,
      product: detail,
      cantidad: item ? itemQty(item) : 1,
      asiento: item ? first(item?.asiento, item?.seat, "") || "" : "",
      observacion: item
        ? String(first(item?.observacion, item?.nota, "") || "")
        : "",
      condicionCritica: "",
      selected: item ? selectedFromItem(detail, item) : {},
    });
  }

  async function addSimpleProduct(product) {
    setLoadingAction(true);

    try {
      await api.post(`/restaurante/pedidos/${pedido.id}/items`, {
        producto_id: product.id,
        cantidad: 1,
        asiento: null,
        observacion: null,
        modificadores: [],
      });

      await loadPedido();
      showNotice("success", `${product.nombre} agregado.`);
    } catch (error) {
      showNotice(
        "error",
        errorMessage(error, "No fue posible agregar el producto.")
      );
    } finally {
      setLoadingAction(false);
    }
  }

  function toggleModifier(group, option) {
    setEditor((current) => {
      if (!current) return current;

      const selected = {
        ...current.selected,
        [group.id]: {
          ...(current.selected?.[group.id] || {}),
        },
      };

      const groupSelected = selected[group.id];

      if (group.tipo === "single") {
        const alreadySelected = num(groupSelected[option.id], 0) > 0;

        selected[group.id] = alreadySelected
          ? {}
          : { [option.id]: 1 };
      } else if (group.tipo === "quantity") {
        const currentQty = num(groupSelected[option.id], 0);

        groupSelected[option.id] =
          currentQty > 0
            ? 0
            : 1;
      } else {
        const currentQty = num(groupSelected[option.id], 0);
        const nextValue = currentQty > 0 ? 0 : 1;

        if (
          nextValue > 0 &&
          group.maximo > 0 &&
          selectedCount(group, selected) >= group.maximo
        ) {
          return current;
        }

        groupSelected[option.id] = nextValue;
      }

      return {
        ...current,
        selected,
      };
    });
  }

  function changeModifierQuantity(group, option, delta) {
    setEditor((current) => {
      if (!current) return current;

      const selected = {
        ...current.selected,
        [group.id]: {
          ...(current.selected?.[group.id] || {}),
        },
      };

      const currentQty = num(selected[group.id][option.id], 0);
      selected[group.id][option.id] = Math.max(
        0,
        Math.min(option.maxCantidad || 99, currentQty + delta)
      );

      return {
        ...current,
        selected,
      };
    });
  }

  async function saveEditor() {
    if (!editor || !pedido?.id) return;

    if (!hasValidModifierSelection(editor.product, editor.selected)) {
      showNotice(
        "error",
        "Revisa las opciones obligatorias antes de agregar el producto."
      );
      return;
    }

    const observationParts = [];

    if (editor.observacion.trim()) {
      observationParts.push(editor.observacion.trim());
    }

    if (editor.condicionCritica.trim()) {
      observationParts.push(
        `⚠ CONDICIÓN / ALERGIA: ${editor.condicionCritica.trim()}`
      );
    }

    const body = {
      producto_id: editor.product.id,
      cantidad: Math.max(1, num(editor.cantidad, 1)),
      asiento:
        String(editor.asiento || "").trim() ||
        null,
      observacion:
        observationParts.join(" · ") ||
        null,
      modificadores: modifierPayload(editor.product, editor.selected),
    };

    setLoadingAction(true);

    try {
      if (editor.mode === "edit" && editor.item) {
        await api.patch(
          `/restaurante/pedidos/${pedido.id}/items/${itemId(editor.item)}`,
          body
        );
      } else {
        await api.post(`/restaurante/pedidos/${pedido.id}/items`, body);
      }

      setEditor(null);
      await loadPedido();

      showNotice(
        "success",
        editor.mode === "edit"
          ? "Producto actualizado."
          : "Producto agregado."
      );
    } catch (error) {
      showNotice(
        "error",
        errorMessage(error, "No fue posible guardar el producto.")
      );
    } finally {
      setLoadingAction(false);
    }
  }

  async function removeItem(item) {
    if (!pedido?.id || !itemId(item)) return;

    const confirmed = window.confirm(`¿Quitar ${itemName(item)} del pedido?`);
    if (!confirmed) return;

    setLoadingAction(true);

    try {
      await api.delete(
        `/restaurante/pedidos/${pedido.id}/items/${itemId(item)}`
      );

      await loadPedido();
      showNotice("success", "Producto eliminado.");
    } catch (error) {
      showNotice(
        "error",
        errorMessage(error, "No fue posible eliminar el producto.")
      );
    } finally {
      setLoadingAction(false);
    }
  }

  async function editItem(item) {
    const productId = itemProductId(item);
    let product = productosById.get(productId);

    if (!product && productId) {
      try {
        const response = await api.get(`/inventario_productos/${productId}`);
        const raw =
          response?.data?.producto ||
          response?.data?.data ||
          response?.data;

        if (raw && typeof raw === "object") {
          product = normalizeProduct(raw);
        }
      } catch (error) {
        showNotice(
          "error",
          errorMessage(error, "No fue posible cargar el producto del pedido.")
        );
        return;
      }
    }

    if (!product) {
      showNotice(
        "error",
        "El producto del pedido ya no está disponible en inventario."
      );
      return;
    }

    await openProduct(product, item);
  }

  async function sendToKitchen() {
    if (!pedido?.id || !itemsBorrador.length || cuentaSolicitada) return;

    setLoadingAction(true);

    try {
      await api.post(`/restaurante/pedidos/${pedido.id}/enviar`);

      await loadPedido();
      loadSalon({ silent: true });
      setPedidoAbiertoMobile(false);
      showNotice("success", "Pedido enviado correctamente.");
    } catch (error) {
      showNotice(
        "error",
        errorMessage(error, "No fue posible enviar el pedido a cocina.")
      );
    } finally {
      setLoadingAction(false);
    }
  }

  function abrirSolicitudCuenta() {
    if (!pedido?.id) return;

    if (itemsBorrador.length) {
      showNotice(
        "error",
        `Hay ${itemsBorrador.length} producto${itemsBorrador.length === 1 ? "" : "s"} sin enviar.`
      );
      return;
    }

    if (itemsEnCurso.length) {
      showNotice(
        "error",
        `Aún hay ${itemsEnCurso.length} producto${itemsEnCurso.length === 1 ? "" : "s"} pendiente${itemsEnCurso.length === 1 ? "" : "s"} de servicio.`
      );
      return;
    }

    if (!pedidoItems.length) {
      showNotice("error", "No puedes solicitar la cuenta de un pedido vacío.");
      return;
    }

    setCuentaModalOpen(true);
  }

  async function solicitarCuenta() {
    if (!pedido?.id || !puedeSolicitarCuenta) return;

    setLoadingAction(true);

    try {
      await api.post(
        `/restaurante/pedidos/${pedido.id}/solicitar-cuenta`
      );

      setCuentaModalOpen(false);
      await loadPedido();
      loadSalon({ silent: true });

      window.dispatchEvent(
        new CustomEvent("tavolata:notifications-refresh")
      );

      showNotice(
        "success",
        `${pedido?.mesa_nombre || "La mesa"} quedó lista para cobro en Caja.`
      );
    } catch (error) {
      showNotice(
        "error",
        errorMessage(error, "No fue posible solicitar la cuenta.")
      );
    } finally {
      setLoadingAction(false);
    }
  }

  const editorUnitPrice = editor
    ? editor.product.precio + modifierExtra(editor.product, editor.selected)
    : 0;

  const editorTotal = editor
    ? editorUnitPrice * Math.max(1, num(editor.cantidad, 1))
    : 0;

  return (
    <div className="mes-shell">
      <style>{`
        * { box-sizing: border-box; }

        :root {
          --mes-bg: #f6f7f8;
          --mes-paper: #ffffff;
          --mes-text: #172033;
          --mes-muted: #687385;
          --mes-line: #e5e9ee;
          --mes-brand: #b86d2f;
          --mes-brand-dark: #8e4f1f;
          --mes-brand-soft: #fff3e8;
          --mes-green: #198754;
          --mes-danger: #c93b3b;
          --mes-shadow: 0 12px 36px rgba(30, 41, 59, .08);
        }

        body { overscroll-behavior-y: contain; }

        button, input, textarea {
          font: inherit;
        }

        button {
          -webkit-tap-highlight-color: transparent;
        }

        .mes-shell {
          min-height: 100vh;
          min-height: 100dvh;
          background: var(--mes-bg);
          color: var(--mes-text);
          font-family:
            Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont,
            "Segoe UI", sans-serif;
        }

        .mes-page {
          width: min(1600px, 100%);
          margin: 0 auto;
          padding: 18px;
          padding-bottom: calc(98px + env(safe-area-inset-bottom));
        }

        .mes-topbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          margin-bottom: 14px;
        }

        .mes-title {
          min-width: 0;
        }

        .mes-title h1 {
          margin: 0;
          font-size: clamp(22px, 2.2vw, 30px);
          line-height: 1.05;
          letter-spacing: -.03em;
        }

        .mes-title p {
          margin: 5px 0 0;
          color: var(--mes-muted);
          font-size: 13px;
        }

        .mes-pill {
          min-height: 44px;
          padding: 8px 14px;
          border: 1px solid var(--mes-line);
          border-radius: 999px;
          background: #fff;
          font-weight: 800;
          white-space: nowrap;
        }


        .mes-table-selector {
          width: min(1320px, 100%);
          margin: 0 auto;
        }

        .mes-selector-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 14px;
          margin-bottom: 14px;
        }

        .mes-selector-head h1 {
          margin: 0;
          font-size: clamp(24px, 3vw, 32px);
          letter-spacing: -.035em;
        }

        .mes-selector-head p {
          margin: 6px 0 0;
          color: var(--mes-muted);
          font-size: 13px;
        }

        .mes-refresh {
          min-height: 44px;
          padding: 0 14px;
          border: 1px solid var(--mes-line);
          border-radius: 12px;
          background: #fff;
          color: #475569;
          font-weight: 850;
          cursor: pointer;
          white-space: nowrap;
        }

        .mes-selector-metrics {
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          gap: 9px;
          margin-bottom: 12px;
        }

        .mes-metric {
          min-width: 0;
          padding: 10px 12px;
          border: 1px solid var(--mes-line);
          border-radius: 13px;
          background: #fff;
        }

        .mes-metric small {
          display: block;
          color: var(--mes-muted);
          font-size: 11px;
          font-weight: 750;
        }

        .mes-metric strong {
          display: block;
          margin-top: 2px;
          font-size: 21px;
          line-height: 1;
        }

        .mes-selector-panel {
          overflow: hidden;
          border: 1px solid var(--mes-line);
          border-radius: 18px;
          background: #fff;
          box-shadow: var(--mes-shadow);
        }

        .mes-selector-section {
          padding: 11px 13px;
          border-bottom: 1px solid var(--mes-line);
        }

        .mes-selector-label {
          margin-bottom: 8px;
          color: var(--mes-muted);
          font-size: 11px;
          font-weight: 850;
          text-transform: uppercase;
          letter-spacing: .045em;
        }

        .mes-selector-scroll {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          scrollbar-width: thin;
        }

        .mes-filter-button {
          min-height: 42px;
          flex: 0 0 auto;
          border: 1px solid #dce2e8;
          border-radius: 11px;
          padding: 0 13px;
          background: #fff;
          color: #475569;
          font-weight: 850;
          cursor: pointer;
        }

        .mes-filter-button.active {
          border-color: #d69b62;
          background: var(--mes-brand-soft);
          color: var(--mes-brand-dark);
        }

        .mes-tables-area {
          padding: 13px;
        }

        .mes-tables-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(205px, 1fr));
          gap: 11px;
        }

        .mes-table-card {
          min-width: 0;
          min-height: 150px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          gap: 12px;
          padding: 14px;
          border: 1px solid #e0e5ea;
          border-radius: 16px;
          background: #fff;
          text-align: left;
          cursor: pointer;
          transition: transform .1s ease, box-shadow .12s ease, border-color .12s ease;
        }

        .mes-table-card:active {
          transform: scale(.99);
        }

        @media (hover:hover) {
          .mes-table-card:hover {
            border-color: #d5aa82;
            box-shadow: 0 9px 24px rgba(40, 32, 24, .08);
          }
        }

        .mes-table-card.fuera {
          opacity: .58;
          cursor: not-allowed;
        }

        .mes-table-card.listo {
          border-color: #73c69b;
          box-shadow: 0 0 0 2px rgba(25, 135, 84, .06);
        }

        .mes-table-card.cuenta {
          border-color: #c7a7e8;
        }

        .mes-table-card-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 9px;
        }

        .mes-table-name {
          min-width: 0;
        }

        .mes-table-name strong {
          display: block;
          font-size: 17px;
          line-height: 1.15;
          overflow-wrap: anywhere;
        }

        .mes-table-name small {
          display: block;
          margin-top: 4px;
          color: var(--mes-muted);
          font-size: 11px;
        }

        .mes-state {
          min-height: 28px;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 0 9px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 900;
          white-space: nowrap;
        }

        .mes-state.libre { background:#e6f7ed; color:#17663d; }
        .mes-state.reservada { background:#e8f0ff; color:#2856a3; }
        .mes-state.abierta { background:#fff1dd; color:#9a5617; }
        .mes-state.ocupada { background:#fee9e7; color:#a52e28; }
        .mes-state.cocina { background:#fff0db; color:#a55710; }
        .mes-state.listo { background:#dff7e9; color:#12683c; }
        .mes-state.cuenta { background:#f2e8ff; color:#7431a8; }
        .mes-state.fuera { background:#e9edf1; color:#596474; }

        .mes-table-info {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 7px;
        }

        .mes-table-info-item {
          min-width: 0;
          padding: 8px 9px;
          border-radius: 10px;
          background: #f7f8fa;
        }

        .mes-table-info-item small {
          display: block;
          color: var(--mes-muted);
          font-size: 10px;
        }

        .mes-table-info-item strong {
          display: block;
          margin-top: 2px;
          font-size: 13px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .mes-table-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          color: var(--mes-muted);
          font-size: 11px;
          font-weight: 750;
        }

        .mes-ready-badge {
          min-height: 28px;
          display: inline-flex;
          align-items: center;
          padding: 0 9px;
          border-radius: 999px;
          background: #dff7e9;
          color: #12683c;
          font-weight: 900;
        }

        .mes-back-button {
          min-height: 44px;
          border: 1px solid var(--mes-line);
          border-radius: 12px;
          padding: 0 13px;
          background: #fff;
          color: #475569;
          font-weight: 900;
          cursor: pointer;
        }

        .mes-order-title-row {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
        }

        .mes-person-count {
          display: grid;
          grid-template-columns: 52px minmax(90px, 1fr) 52px;
          gap: 10px;
          align-items: center;
          margin: 18px 0;
        }

        .mes-person-count button {
          height: 52px;
          border: 1px solid #dce2e8;
          border-radius: 13px;
          background: #fff;
          color: var(--mes-text);
          font-size: 24px;
          font-weight: 900;
          cursor: pointer;
        }

        .mes-person-value {
          height: 58px;
          display: grid;
          place-items: center;
          border-radius: 13px;
          background: #f7f8fa;
          font-size: 28px;
          font-weight: 950;
        }

        .mes-layout {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(330px, 410px);
          gap: 16px;
          align-items: start;
        }

        .mes-catalog,
        .mes-order {
          background: var(--mes-paper);
          border: 1px solid var(--mes-line);
          border-radius: 18px;
          box-shadow: var(--mes-shadow);
        }

        .mes-catalog {
          min-width: 0;
          overflow: hidden;
        }

        .mes-tools {
          padding: 14px;
          border-bottom: 1px solid var(--mes-line);
        }

        .mes-search {
          width: 100%;
          min-height: 48px;
          border: 1px solid #d9dee6;
          border-radius: 13px;
          padding: 0 14px;
          outline: none;
          background: #fff;
          color: var(--mes-text);
          font-size: 16px;
        }

        .mes-search:focus {
          border-color: var(--mes-brand);
          box-shadow: 0 0 0 3px rgba(184, 109, 47, .10);
        }

        .mes-categories-wrap {
          position: relative;
          border-bottom: 1px solid var(--mes-line);
          background: #fff;
        }

        .mes-categories {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          padding: 11px 13px;
          scrollbar-width: thin;
          scroll-snap-type: x proximity;
        }

        .mes-category {
          flex: 0 0 auto;
          min-height: 44px;
          padding: 0 15px;
          border: 1px solid #dce2e8;
          border-radius: 12px;
          background: #fff;
          color: #42506a;
          font-weight: 800;
          cursor: pointer;
          scroll-snap-align: start;
        }

        .mes-category.active {
          background: var(--mes-brand-soft);
          border-color: #e6b98d;
          color: var(--mes-brand-dark);
        }

        .mes-category.featured {
          border-color: #e4b970;
        }

        .mes-category.featured.active {
          background: #fff3d6;
          color: #865b00;
        }

        .mes-product-area {
          padding: 14px;
          min-height: 420px;
        }

        .mes-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          gap: 12px;
        }

        .mes-product {
          position: relative;
          min-width: 0;
          min-height: 148px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          gap: 10px;
          padding: 13px;
          border: 1px solid var(--mes-line);
          border-radius: 15px;
          background: #fff;
          cursor: pointer;
          text-align: left;
          transition: border-color .12s ease, box-shadow .12s ease;
        }

        .mes-product:active {
          transform: scale(.992);
        }

        @media (hover:hover) {
          .mes-product:hover {
            border-color: #d8b08a;
            box-shadow: 0 8px 24px rgba(60, 38, 18, .08);
          }
        }

        .mes-product-top {
          display: flex;
          gap: 10px;
          min-width: 0;
        }

        .mes-image {
          width: 58px;
          height: 58px;
          flex: 0 0 58px;
          border-radius: 12px;
          background: #f2f4f6;
          object-fit: cover;
          overflow: hidden;
        }

        .mes-image.empty {
          display: grid;
          place-items: center;
          color: #9aa3b1;
          font-size: 22px;
        }

        .mes-product-name {
          margin: 0;
          font-size: 14px;
          line-height: 1.25;
          font-weight: 900;
          overflow-wrap: anywhere;
        }

        .mes-product-sku {
          margin-top: 5px;
          color: var(--mes-muted);
          font-size: 11px;
        }

        .mes-product-bottom {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .mes-price {
          font-size: 18px;
          font-weight: 950;
          letter-spacing: -.02em;
        }

        .mes-add {
          width: 44px;
          height: 44px;
          display: grid;
          place-items: center;
          flex: 0 0 44px;
          border: 0;
          border-radius: 12px;
          background: var(--mes-brand);
          color: #fff;
          font-size: 24px;
          font-weight: 500;
          cursor: pointer;
        }

        .mes-order {
          position: sticky;
          top: 14px;
          overflow: hidden;
          max-height: calc(100dvh - 28px);
          display: flex;
          flex-direction: column;
        }

        .mes-order-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 15px;
          border-bottom: 1px solid var(--mes-line);
        }

        .mes-order-head h2 {
          margin: 0;
          font-size: 18px;
        }

        .mes-order-head small {
          display: block;
          margin-top: 4px;
          color: var(--mes-muted);
        }

        .mes-order-items {
          overflow-y: auto;
          padding: 10px 12px;
          flex: 1;
          min-height: 180px;
        }

        .mes-order-item {
          padding: 12px 4px;
          border-bottom: 1px solid #edf0f3;
        }

        .mes-order-item:last-child {
          border-bottom: 0;
        }

        .mes-item-main {
          display: flex;
          gap: 10px;
          justify-content: space-between;
        }

        .mes-item-name {
          min-width: 0;
          font-size: 14px;
          font-weight: 850;
          line-height: 1.3;
        }

        .mes-item-total {
          flex: 0 0 auto;
          font-weight: 900;
        }

        .mes-item-note {
          margin-top: 5px;
          color: var(--mes-muted);
          font-size: 12px;
          line-height: 1.35;
        }

        .mes-item-actions {
          display: flex;
          gap: 8px;
          margin-top: 8px;
        }

        .mes-mini {
          min-height: 36px;
          border: 1px solid #dfe4ea;
          border-radius: 9px;
          padding: 0 11px;
          background: #fff;
          color: #445168;
          font-weight: 800;
          cursor: pointer;
        }

        .mes-mini.danger {
          color: var(--mes-danger);
        }

        .mes-order-footer {
          border-top: 1px solid var(--mes-line);
          padding: 14px;
          background: #fff;
          padding-bottom: calc(14px + env(safe-area-inset-bottom));
        }

        .mes-total-row {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          margin-bottom: 12px;
        }

        .mes-total-row span {
          color: var(--mes-muted);
          font-size: 13px;
        }

        .mes-total-row strong {
          font-size: 23px;
        }

        .mes-primary {
          width: 100%;
          min-height: 50px;
          border: 0;
          border-radius: 13px;
          padding: 0 16px;
          background: var(--mes-brand);
          color: #fff;
          font-weight: 900;
          cursor: pointer;
        }

        .mes-primary:disabled,
        .mes-add:disabled {
          opacity: .5;
          cursor: not-allowed;
        }

        .mes-empty {
          min-height: 220px;
          display: grid;
          place-items: center;
          text-align: center;
          padding: 28px;
          color: var(--mes-muted);
        }

        .mes-loading {
          min-height: 300px;
          display: grid;
          place-items: center;
          color: var(--mes-muted);
        }

        .mes-pagination {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-top: 14px;
          padding: 12px 4px 2px;
        }

        .mes-pagination-info {
          color: var(--mes-muted);
          font-size: 12px;
          line-height: 1.35;
        }

        .mes-pagination-actions {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .mes-page-button {
          min-width: 44px;
          min-height: 44px;
          border: 1px solid #dce2e8;
          border-radius: 11px;
          background: #fff;
          color: var(--mes-text);
          font-weight: 850;
          cursor: pointer;
        }

        .mes-page-button:disabled {
          opacity: .38;
          cursor: not-allowed;
        }

        .mes-page-current {
          min-width: 72px;
          text-align: center;
          font-size: 13px;
          font-weight: 850;
          color: #475569;
        }

        .mes-request-account {
          width: 100%;
          min-height: 48px;
          margin-top: 9px;
          border: 1px solid #8f63bd;
          border-radius: 12px;
          background: #f6effd;
          color: #6d399d;
          font-weight: 900;
          cursor: pointer;
        }

        .mes-request-account:disabled {
          opacity: .44;
          cursor: not-allowed;
        }

        .mes-account-hint {
          margin-top: 8px;
          color: var(--mes-muted);
          font-size: 11px;
          line-height: 1.4;
          text-align: center;
        }

        .mes-account-ready {
          display: grid;
          gap: 5px;
          padding: 12px;
          border: 1px solid #c9afe1;
          border-radius: 13px;
          background: #f7f0fd;
          color: #60308e;
        }

        .mes-account-ready strong {
          font-size: 14px;
        }

        .mes-account-ready span {
          color: #7b5d98;
          font-size: 12px;
        }

        .mes-account-back {
          min-height: 42px;
          margin-top: 5px;
          border: 0;
          border-radius: 10px;
          background: #7442a4;
          color: #fff;
          font-weight: 900;
          cursor: pointer;
        }

        .mes-account-confirm {
          padding: 6px 0 2px;
        }

        .mes-account-summary {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 9px;
          margin-top: 14px;
        }

        .mes-account-summary > div {
          padding: 11px;
          border-radius: 12px;
          background: #f7f8fa;
        }

        .mes-account-summary small {
          display: block;
          color: var(--mes-muted);
          font-size: 10px;
        }

        .mes-account-summary strong {
          display: block;
          margin-top: 3px;
          font-size: 15px;
        }

        .mes-mobile-bar {
          display: none;
        }

        .mes-backdrop {
          position: fixed;
          inset: 0;
          z-index: 1000;
          background: rgba(15, 23, 42, .48);
          display: grid;
          place-items: center;
          padding: 18px;
        }

        .mes-editor {
          width: min(720px, 100%);
          max-height: min(90dvh, 900px);
          display: flex;
          flex-direction: column;
          border-radius: 20px;
          overflow: hidden;
          background: #fff;
          box-shadow: 0 30px 90px rgba(15, 23, 42, .25);
        }

        .mes-editor-head {
          display: flex;
          gap: 12px;
          justify-content: space-between;
          align-items: flex-start;
          padding: 17px;
          border-bottom: 1px solid var(--mes-line);
        }

        .mes-editor-head h2 {
          margin: 0;
          font-size: 20px;
          line-height: 1.2;
        }

        .mes-editor-head p {
          margin: 5px 0 0;
          color: var(--mes-muted);
          font-size: 13px;
        }

        .mes-close {
          width: 44px;
          height: 44px;
          border: 1px solid var(--mes-line);
          border-radius: 12px;
          background: #fff;
          cursor: pointer;
          font-size: 22px;
        }

        .mes-editor-body {
          overflow-y: auto;
          padding: 16px;
          flex: 1;
        }

        .mes-group {
          margin-bottom: 18px;
        }

        .mes-group-title {
          display: flex;
          justify-content: space-between;
          gap: 10px;
          margin-bottom: 9px;
        }

        .mes-group-title strong {
          font-size: 14px;
        }

        .mes-required {
          color: #ad5b17;
          font-size: 11px;
          font-weight: 800;
        }

        .mes-options {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px;
        }

        .mes-option {
          min-height: 48px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          padding: 9px 11px;
          border: 1px solid #dfe4ea;
          border-radius: 11px;
          background: #fff;
          cursor: pointer;
          text-align: left;
        }

        .mes-option.selected {
          border-color: #d79b62;
          background: var(--mes-brand-soft);
        }

        .mes-option-name {
          min-width: 0;
          font-size: 13px;
          font-weight: 800;
        }

        .mes-option-price {
          flex: 0 0 auto;
          color: var(--mes-muted);
          font-size: 11px;
        }

        .mes-qty {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .mes-qty button {
          width: 38px;
          height: 38px;
          border: 1px solid #dce2e8;
          border-radius: 10px;
          background: #fff;
          cursor: pointer;
          font-size: 19px;
        }

        .mes-fields {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-top: 10px;
        }

        .mes-field label {
          display: block;
          margin-bottom: 6px;
          color: #5a6678;
          font-size: 12px;
          font-weight: 800;
        }

        .mes-field input,
        .mes-field textarea {
          width: 100%;
          min-height: 46px;
          border: 1px solid #dce2e8;
          border-radius: 11px;
          padding: 10px 12px;
          outline: none;
          font-size: 16px;
          resize: vertical;
        }

        .mes-field textarea {
          min-height: 86px;
        }

        .mes-critical textarea {
          border-color: #e5afaf;
          background: #fffafa;
        }

        .mes-editor-footer {
          display: flex;
          gap: 10px;
          align-items: center;
          padding: 13px 16px;
          padding-bottom: calc(13px + env(safe-area-inset-bottom));
          border-top: 1px solid var(--mes-line);
          background: #fff;
        }

        .mes-editor-total {
          flex: 1;
          min-width: 0;
        }

        .mes-editor-total small {
          display: block;
          color: var(--mes-muted);
        }

        .mes-editor-total strong {
          display: block;
          font-size: 20px;
        }

        .mes-save {
          min-height: 50px;
          padding: 0 20px;
          border: 0;
          border-radius: 13px;
          background: var(--mes-brand);
          color: #fff;
          font-weight: 900;
          cursor: pointer;
        }

        .mes-notice {
          position: fixed;
          z-index: 1400;
          right: 18px;
          bottom: calc(18px + env(safe-area-inset-bottom));
          max-width: min(420px, calc(100vw - 36px));
          padding: 12px 15px;
          border-radius: 12px;
          color: #fff;
          font-weight: 800;
          box-shadow: 0 16px 40px rgba(15, 23, 42, .24);
        }

        .mes-notice.success { background: #18794e; }
        .mes-notice.error { background: #b42318; }

        @media (max-width: 1050px) {
          .mes-layout {
            grid-template-columns: minmax(0, 1fr) 340px;
          }

          .mes-grid {
            grid-template-columns: repeat(auto-fill, minmax(165px, 1fr));
          }
        }

        @media (max-width: 820px) {
          .mes-page {
            padding: 12px;
            padding-bottom: calc(88px + env(safe-area-inset-bottom));
          }

          .mes-topbar {
            align-items: flex-start;
          }

          .mes-layout {
            display: block;
          }

          .mes-order {
            display: none;
          }

          .mes-order.mobile-open {
            display: flex;
            position: fixed;
            inset: 0;
            z-index: 1200;
            max-height: 100dvh;
            border: 0;
            border-radius: 0;
            box-shadow: none;
          }

          .mes-mobile-bar {
            position: fixed;
            z-index: 900;
            left: 10px;
            right: 10px;
            bottom: calc(10px + env(safe-area-inset-bottom));
            min-height: 62px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: 9px 10px 9px 14px;
            border: 1px solid #e2e6eb;
            border-radius: 16px;
            background: rgba(255,255,255,.97);
            box-shadow: 0 14px 42px rgba(15, 23, 42, .18);
            backdrop-filter: blur(14px);
          }

          .mes-mobile-summary {
            min-width: 0;
          }

          .mes-mobile-summary small {
            display: block;
            color: var(--mes-muted);
            font-size: 11px;
          }

          .mes-mobile-summary strong {
            display: block;
            font-size: 18px;
          }

          .mes-mobile-button {
            min-height: 46px;
            border: 0;
            border-radius: 12px;
            padding: 0 15px;
            background: var(--mes-brand);
            color: #fff;
            font-weight: 900;
          }

          .mes-editor {
            width: 100%;
            height: 100dvh;
            max-height: 100dvh;
            border-radius: 0;
          }

          .mes-backdrop {
            padding: 0;
          }

          .mes-options {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 520px) {
          .mes-selector-metrics {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }

          .mes-selector-metrics .mes-metric:nth-child(4),
          .mes-selector-metrics .mes-metric:nth-child(5) {
            display: none;
          }

          .mes-tables-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 8px;
          }

          .mes-table-card {
            min-height: 142px;
            padding: 11px;
          }

          .mes-table-card-head {
            display: block;
          }

          .mes-state {
            margin-top: 8px;
          }

          .mes-table-info {
            grid-template-columns: 1fr;
          }

          .mes-table-info-item:nth-child(2) {
            display: none;
          }

          .mes-selector-head {
            align-items: center;
          }

          .mes-selector-head p {
            max-width: 230px;
          }

          .mes-title h1 {
            font-size: 22px;
          }

          .mes-pill {
            min-height: 40px;
            padding: 7px 10px;
            font-size: 12px;
          }

          .mes-tools {
            padding: 10px;
          }

          .mes-categories {
            padding: 9px 10px;
          }

          .mes-product-area {
            padding: 10px;
          }

          .mes-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 8px;
          }

          .mes-product {
            min-height: 154px;
            padding: 10px;
          }

          .mes-product-top {
            display: block;
          }

          .mes-image {
            width: 48px;
            height: 48px;
            margin-bottom: 8px;
          }

          .mes-product-name {
            font-size: 13px;
          }

          .mes-product-sku {
            display: none;
          }

          .mes-price {
            font-size: 16px;
          }

          .mes-fields {
            grid-template-columns: 1fr;
          }

          .mes-pagination {
            align-items: flex-start;
            flex-direction: column;
          }

          .mes-pagination-actions {
            width: 100%;
            justify-content: space-between;
          }

          .mes-editor-footer {
            align-items: stretch;
          }

          .mes-save {
            padding: 0 14px;
          }
        }
      `}</style>

      {vista === "mesas" ? (
        <main className="mes-page mes-table-selector">
          <header className="mes-selector-head">
            <div>
              <h1>Mesero</h1>
              <p>Selecciona una mesa para comenzar o continuar la atención.</p>
            </div>

            <button
              type="button"
              className="mes-refresh"
              onClick={() => loadSalon({ silent: true })}
              disabled={refrescandoSalon}
            >
              {refrescandoSalon ? "Actualizando…" : "Actualizar"}
            </button>
          </header>

          <div className="mes-selector-metrics">
            <div className="mes-metric">
              <small>Mesas</small>
              <strong>{metricasMesas.total}</strong>
            </div>
            <div className="mes-metric">
              <small>Libres</small>
              <strong>{metricasMesas.libres}</strong>
            </div>
            <div className="mes-metric">
              <small>Activas</small>
              <strong>{metricasMesas.activas}</strong>
            </div>
            <div className="mes-metric">
              <small>Listas</small>
              <strong>{metricasMesas.listas}</strong>
            </div>
            <div className="mes-metric">
              <small>Cuenta</small>
              <strong>{metricasMesas.cuenta}</strong>
            </div>
          </div>

          <section className="mes-selector-panel">
            <div className="mes-selector-section">
              <div className="mes-selector-label">Zona</div>
              <div className="mes-selector-scroll">
                <button
                  type="button"
                  className={`mes-filter-button ${zonaSalonActiva === "TODAS" ? "active" : ""}`}
                  onClick={() => setZonaSalonActiva("TODAS")}
                >
                  Todas
                </button>

                {zonasSalon.map((zona) => (
                  <button
                    type="button"
                    key={zona.id}
                    className={`mes-filter-button ${zonaSalonActiva === zona.id ? "active" : ""}`}
                    onClick={() => setZonaSalonActiva(zona.id)}
                  >
                    {zona.nombre}
                  </button>
                ))}
              </div>
            </div>

            <div className="mes-selector-section">
              <div className="mes-selector-label">Estado</div>
              <div className="mes-selector-scroll">
                {[
                  ["TODAS", "Todas"],
                  ["LIBRES", "Libres"],
                  ["ACTIVAS", "Con pedido"],
                  ["LISTAS", `Listas${metricasMesas.listas ? ` · ${metricasMesas.listas}` : ""}`],
                  ["CUENTA", "Cuenta"],
                ].map(([value, label]) => (
                  <button
                    type="button"
                    key={value}
                    className={`mes-filter-button ${filtroMesas === value ? "active" : ""}`}
                    onClick={() => setFiltroMesas(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mes-tables-area">
              {loadingSalon ? (
                <div className="mes-loading">Cargando mesas…</div>
              ) : mesasVisibles.length ? (
                <div className="mes-tables-grid">
                  {mesasVisibles.map((mesa) => {
                    const meta = waiterMesaMeta(mesa);
                    const state = waiterMesaState(mesa);
                    const disabled = state === "FUERA_SERVICIO";

                    return (
                      <button
                        type="button"
                        key={mesa.id}
                        className={`mes-table-card ${meta.className}`}
                        onClick={() => seleccionarMesa(mesa)}
                        disabled={disabled}
                      >
                        <div className="mes-table-card-head">
                          <div className="mes-table-name">
                            <strong>{mesa.nombre}</strong>
                            <small>
                              {mesa.codigo || `Mesa ${mesa.id}`} · {mesa.zonaNombre}
                            </small>
                          </div>

                          <span className={`mes-state ${meta.className}`}>
                            {meta.icon} {meta.label}
                          </span>
                        </div>

                        <div className="mes-table-info">
                          <div className="mes-table-info-item">
                            <small>Personas</small>
                            <strong>
                              {mesa.pedidoId
                                ? `${mesa.comensales || 0} / ${mesa.capacidad}`
                                : `hasta ${mesa.capacidad}`}
                            </strong>
                          </div>

                          <div className="mes-table-info-item">
                            <small>
                              {mesa.pedidoId ? "Total actual" : "Pedido"}
                            </small>
                            <strong>
                              {mesa.pedidoId
                                ? money(mesa.total)
                                : "Nuevo"}
                            </strong>
                          </div>
                        </div>

                        <div className="mes-table-footer">
                          <span>
                            {mesa.pedidoId
                              ? `${mesa.minutos || 0} min · Continuar`
                              : state === "RESERVADA"
                                ? "Abrir reserva"
                                : "Tocar para abrir"}
                          </span>

                          {mesa.itemsListos > 0 && (
                            <span className="mes-ready-badge">
                              {mesa.itemsListos} listo{mesa.itemsListos === 1 ? "" : "s"}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="mes-empty">
                  <div>
                    <strong>No hay mesas para este filtro.</strong>
                    <div style={{ marginTop: 5 }}>
                      Prueba otra zona o estado.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        </main>
      ) : (
        <>
          <main className="mes-page">
            <header className="mes-topbar">
              <div className="mes-order-title-row">
                <button
                  type="button"
                  className="mes-back-button"
                  onClick={volverAMesas}
                >
                  ← Mesas
                </button>

                <div className="mes-title">
                  <h1>{pedido?.mesa_nombre || "Mesero"}</h1>
                  <p>
                    {pedido?.zona_nombre ? `${pedido.zona_nombre} · ` : ""}
                    {pedido?.comensales
                      ? `${pedido.comensales} persona${num(pedido.comensales, 0) === 1 ? "" : "s"}`
                      : mesaIdActivo
                        ? `Mesa ${mesaIdActivo}`
                        : "Toma de pedido"}
                    {pedido?.id ? ` · Pedido #${pedido.numero || pedido.id}` : ""}
                  </p>
                </div>
              </div>

              <button className="mes-pill" type="button" onClick={() => loadPedido()}>
                {loadingPedido ? "Actualizando…" : `${cantidadItems} productos`}
              </button>
            </header>

            <div className="mes-layout">
              <section className="mes-catalog">
                <div className="mes-tools">
                  <input
                    className="mes-search"
                    value={busqueda}
                    onChange={(event) => setBusqueda(event.target.value)}
                    placeholder="Buscar producto o SKU…"
                    inputMode="search"
                    autoComplete="off"
                  />
                </div>

                <div className="mes-categories-wrap">
                  <div className="mes-categories">
                    {categorias.map((category) => (
                      <button
                        key={category.id}
                        type="button"
                        className={[
                          "mes-category",
                          category.id === categoriaActiva ? "active" : "",
                          category.id === DESTACADOS_ID ? "featured" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={() => {
                          setBusqueda("");
                          setBusquedaDebounced("");
                          setPagina(1);
                          setCategoriaActiva(category.id);
                        }}
                      >
                        {category.nombre}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mes-product-area">
                  {loadingCatalogo ? (
                    <div className="mes-loading">Cargando carta…</div>
                  ) : productosVisibles.length ? (
                    <>
                      <div className="mes-grid">
                        {productosVisibles.map((product) => (
                          <button
                            type="button"
                            className="mes-product"
                            key={product.id}
                            onClick={() => openProduct(product)}
                            disabled={loadingAction || !pedido?.id || cuentaSolicitada}
                          >
                            <div className="mes-product-top">
                              {product.imagen ? (
                                <img
                                  className="mes-image"
                                  src={product.imagen}
                                  alt=""
                                  loading="lazy"
                                />
                              ) : (
                                <div className="mes-image empty">🍽</div>
                              )}

                              <div style={{ minWidth: 0 }}>
                                <p className="mes-product-name">{product.nombre}</p>
                                {product.sku && (
                                  <div className="mes-product-sku">{product.sku}</div>
                                )}
                              </div>
                            </div>

                            <div className="mes-product-bottom">
                              <span className="mes-price">{money(product.precio)}</span>
                              <span className="mes-add" aria-hidden="true">
                                +
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>

                      <div className="mes-pagination">
                        <div className="mes-pagination-info">
                          {categoriaActiva === DESTACADOS_ID && !busquedaDebounced
                            ? `${totalProductos} producto${totalProductos === 1 ? "" : "s"} destacado${totalProductos === 1 ? "" : "s"}`
                            : `${totalProductos} producto${totalProductos === 1 ? "" : "s"} disponibles`}
                        </div>

                        {totalPaginas > 1 && (
                          <div className="mes-pagination-actions">
                            <button
                              type="button"
                              className="mes-page-button"
                              disabled={pagina <= 1 || loadingCatalogo}
                              onClick={() => setPagina((current) => Math.max(1, current - 1))}
                              aria-label="Página anterior"
                            >
                              ‹
                            </button>

                            <div className="mes-page-current">
                              {pagina} / {totalPaginas}
                            </div>

                            <button
                              type="button"
                              className="mes-page-button"
                              disabled={pagina >= totalPaginas || loadingCatalogo}
                              onClick={() =>
                                setPagina((current) => Math.min(totalPaginas, current + 1))
                              }
                              aria-label="Página siguiente"
                            >
                              ›
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="mes-empty">
                      <div>
                        <strong>
                          {busqueda
                            ? "No encontramos productos para esa búsqueda."
                            : "No hay productos disponibles en inventario."}
                        </strong>
                        <div style={{ marginTop: 5 }}>
                          {busqueda
                            ? "Prueba con otro nombre, SKU o limpia la búsqueda."
                            : "Revisa el estado de los productos en inventario_productos."}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </section>

              <aside
                className={[
                  "mes-order",
                  pedidoAbiertoMobile ? "mobile-open" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <div className="mes-order-head">
                  <div>
                    <h2>Pedido</h2>
                    <small>
                      {pedido?.id
                        ? `#${pedido.numero || pedido.id} · ${cantidadItems} productos`
                        : "Cargando pedido…"}
                    </small>
                  </div>

                  {pedidoAbiertoMobile && (
                    <button
                      className="mes-close"
                      type="button"
                      onClick={() => setPedidoAbiertoMobile(false)}
                    >
                      ×
                    </button>
                  )}
                </div>

                <div className="mes-order-items">
                  {loadingPedido ? (
                    <div className="mes-loading">Cargando pedido…</div>
                  ) : pedidoItems.length ? (
                    pedidoItems.map((item, index) => (
                      <div className="mes-order-item" key={itemId(item) || index}>
                        <div className="mes-item-main">
                          <div className="mes-item-name">
                            {itemQty(item)} × {itemName(item)}
                          </div>
                          <div className="mes-item-total">
                            {money(itemTotal(item))}
                          </div>
                        </div>

                        <div className="mes-item-note">
                          {String(item?.estado || "").toUpperCase()}
                          {item?.estacion_nombre ? ` · ${item.estacion_nombre}` : ""}
                        </div>

                        {first(item?.observacion, item?.nota) && (
                          <div className="mes-item-note">
                            {first(item?.observacion, item?.nota)}
                          </div>
                        )}

                        {String(item?.estado || "").toUpperCase() === "BORRADOR" && (
                          <div className="mes-item-actions">
                            <button
                              type="button"
                              className="mes-mini"
                              onClick={() => editItem(item)}
                            >
                              Editar
                            </button>

                            <button
                              type="button"
                              className="mes-mini danger"
                              onClick={() => removeItem(item)}
                            >
                              Quitar
                            </button>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="mes-empty">
                      <div>
                        <strong>Pedido vacío</strong>
                        <div style={{ marginTop: 5 }}>
                          Toca un producto para agregarlo.
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mes-order-footer">
                  <div className="mes-total-row">
                    <span>Total actual</span>
                    <strong>{money(pedidoTotal)}</strong>
                  </div>

                  {cuentaSolicitada ? (
                    <div className="mes-account-ready">
                      <strong>✓ Cuenta solicitada</strong>
                      <span>Pedido enviado a Caja · {money(pedidoTotal)}</span>
                      <button
                        type="button"
                        className="mes-account-back"
                        onClick={volverAMesas}
                      >
                        ← Volver a mesas
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="mes-primary"
                        disabled={!pedido?.id || !itemsBorrador.length || loadingAction}
                        onClick={sendToKitchen}
                      >
                        {loadingAction
                          ? "Procesando…"
                          : itemsBorrador.length
                            ? `Enviar ${itemsBorrador.length} a cocina`
                            : "Sin productos nuevos"}
                      </button>

                      <button
                        type="button"
                        className="mes-request-account"
                        disabled={!puedeSolicitarCuenta || loadingAction}
                        onClick={abrirSolicitudCuenta}
                        title={
                          itemsBorrador.length
                            ? "Primero envía los productos nuevos."
                            : itemsEnCurso.length
                              ? "Aún hay productos pendientes de servicio."
                              : "Dejar la mesa lista para cobro."
                        }
                      >
                        Solicitar cuenta
                      </button>

                      {!puedeSolicitarCuenta && !itemsBorrador.length && itemsEnCurso.length > 0 && (
                        <div className="mes-account-hint">
                          Esperando {itemsEnCurso.length} producto{itemsEnCurso.length === 1 ? "" : "s"} antes de solicitar la cuenta.
                        </div>
                      )}
                    </>
                  )}
                </div>
              </aside>
            </div>
          </main>

          <div className="mes-mobile-bar">
            <div className="mes-mobile-summary">
              <small>{cantidadItems} productos</small>
              <strong>{money(pedidoTotal)}</strong>
            </div>

            <button
              type="button"
              className="mes-mobile-button"
              onClick={() => setPedidoAbiertoMobile(true)}
            >
              {cuentaSolicitada ? "Cuenta solicitada" : "Ver pedido"}
            </button>
          </div>
        </>
      )}

      {mesaEnApertura && (
        <div
          className="mes-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !loadingAction) {
              setMesaEnApertura(null);
            }
          }}
        >
          <div className="mes-editor" style={{ width: "min(430px, 100%)" }}>
            <div className="mes-editor-head">
              <div>
                <h2>Abrir {mesaEnApertura.nombre}</h2>
                <p>
                  {mesaEnApertura.zonaNombre} · capacidad {mesaEnApertura.capacidad}
                </p>
              </div>

              <button
                type="button"
                className="mes-close"
                onClick={() => setMesaEnApertura(null)}
                disabled={loadingAction}
              >
                ×
              </button>
            </div>

            <div className="mes-editor-body">
              <div className="mes-group-title">
                <strong>¿Cuántas personas?</strong>
                <span className="mes-required">Puedes ajustarlo después</span>
              </div>

              <div className="mes-person-count">
                <button
                  type="button"
                  onClick={() =>
                    setPersonasApertura((current) => Math.max(1, current - 1))
                  }
                  disabled={loadingAction}
                >
                  −
                </button>

                <div className="mes-person-value">{personasApertura}</div>

                <button
                  type="button"
                  onClick={() =>
                    setPersonasApertura((current) =>
                      Math.min(99, current + 1)
                    )
                  }
                  disabled={loadingAction}
                >
                  +
                </button>
              </div>

              {personasApertura > mesaEnApertura.capacidad && (
                <div className="mes-item-note" style={{ textAlign: "center" }}>
                  La cantidad supera la capacidad configurada de la mesa.
                </div>
              )}
            </div>

            <div className="mes-editor-footer">
              <div className="mes-editor-total">
                <small>Mesa</small>
                <strong>{mesaEnApertura.nombre}</strong>
              </div>

              <button
                type="button"
                className="mes-save"
                onClick={abrirMesa}
                disabled={loadingAction}
              >
                {loadingAction ? "Abriendo…" : "Abrir mesa"}
              </button>
            </div>
          </div>
        </div>
      )}

      {cuentaModalOpen && pedido && (
        <div
          className="mes-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !loadingAction) {
              setCuentaModalOpen(false);
            }
          }}
        >
          <div className="mes-editor" style={{ width: "min(460px, 100%)" }}>
            <div className="mes-editor-head">
              <div>
                <h2>Solicitar cuenta</h2>
                <p>
                  {pedido?.mesa_nombre || `Mesa ${mesaIdActivo}`} · Pedido #{pedido?.numero || pedido?.id}
                </p>
              </div>

              <button
                type="button"
                className="mes-close"
                onClick={() => setCuentaModalOpen(false)}
                disabled={loadingAction}
              >
                ×
              </button>
            </div>

            <div className="mes-editor-body">
              <div className="mes-account-confirm">
                <strong>La mesa quedará pendiente de cobro en Caja.</strong>
                <div className="mes-account-summary">
                  <div>
                    <small>Personas</small>
                    <strong>{pedido?.comensales || 1}</strong>
                  </div>
                  <div>
                    <small>Total</small>
                    <strong>{money(pedidoTotal)}</strong>
                  </div>
                  <div>
                    <small>Productos</small>
                    <strong>{cantidadItems}</strong>
                  </div>
                  <div>
                    <small>Estado</small>
                    <strong>Listo para cobro</strong>
                  </div>
                </div>
              </div>
            </div>

            <div className="mes-editor-footer">
              <button
                type="button"
                className="mes-back-button"
                onClick={() => setCuentaModalOpen(false)}
                disabled={loadingAction}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="mes-save"
                onClick={solicitarCuenta}
                disabled={loadingAction || !puedeSolicitarCuenta}
              >
                {loadingAction ? "Enviando…" : "Solicitar cuenta"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editor && (
        <div
          className="mes-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !loadingAction) {
              setEditor(null);
            }
          }}
        >
          <div className="mes-editor">
            <div className="mes-editor-head">
              <div>
                <h2>{editor.product.nombre}</h2>
                <p>
                  {editor.mode === "edit"
                    ? "Modificar producto del pedido"
                    : "Personalizar producto"}
                  {editorLoading ? " · actualizando opciones…" : ""}
                </p>
              </div>

              <button
                type="button"
                className="mes-close"
                onClick={() => setEditor(null)}
                disabled={loadingAction}
              >
                ×
              </button>
            </div>

            <div className="mes-editor-body">
              {editor.product.grupos.map((group) => (
                <section className="mes-group" key={group.id}>
                  <div className="mes-group-title">
                    <strong>{group.nombre}</strong>
                    {(group.obligatorio || group.minimo > 0) && (
                      <span className="mes-required">
                        {group.obligatorio ? "Obligatorio" : `Mín. ${group.minimo}`}
                      </span>
                    )}
                  </div>

                  <div className="mes-options">
                    {group.opciones.map((option) => {
                      const quantity = num(
                        editor.selected?.[group.id]?.[option.id],
                        0
                      );
                      const selected = quantity > 0;

                      return group.tipo === "quantity" ? (
                        <div
                          className={[
                            "mes-option",
                            selected ? "selected" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          key={option.id}
                        >
                          <div>
                            <div className="mes-option-name">{option.nombre}</div>
                            {option.precioAdicional !== 0 && (
                              <div className="mes-option-price">
                                + {money(option.precioAdicional)}
                              </div>
                            )}
                          </div>

                          <div className="mes-qty">
                            <button
                              type="button"
                              onClick={() =>
                                changeModifierQuantity(group, option, -1)
                              }
                            >
                              −
                            </button>
                            <strong>{quantity}</strong>
                            <button
                              type="button"
                              onClick={() =>
                                changeModifierQuantity(group, option, 1)
                              }
                            >
                              +
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className={[
                            "mes-option",
                            selected ? "selected" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          key={option.id}
                          onClick={() => toggleModifier(group, option)}
                        >
                          <span className="mes-option-name">
                            {selected ? "✓ " : ""}
                            {option.nombre}
                          </span>

                          {option.precioAdicional !== 0 && (
                            <span className="mes-option-price">
                              + {money(option.precioAdicional)}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </section>
              ))}

              <div className="mes-fields">
                <div className="mes-field">
                  <label>Cantidad</label>
                  <input
                    type="number"
                    min="1"
                    max="99"
                    value={editor.cantidad}
                    onChange={(event) =>
                      setEditor((current) => ({
                        ...current,
                        cantidad: Math.max(
                          1,
                          Math.min(99, num(event.target.value, 1))
                        ),
                      }))
                    }
                  />
                </div>

                <div className="mes-field">
                  <label>Asiento / persona</label>
                  <input
                    value={editor.asiento}
                    onChange={(event) =>
                      setEditor((current) => ({
                        ...current,
                        asiento: event.target.value,
                      }))
                    }
                    placeholder="Opcional"
                  />
                </div>
              </div>

              {editor.product.permiteObservacion && (
                <div className="mes-field" style={{ marginTop: 12 }}>
                  <label>Observación</label>
                  <textarea
                    value={editor.observacion}
                    onChange={(event) =>
                      setEditor((current) => ({
                        ...current,
                        observacion: event.target.value,
                      }))
                    }
                    placeholder="Ej.: sin sal, servir salsa aparte…"
                  />
                </div>
              )}

              <div
                className="mes-field mes-critical"
                style={{ marginTop: 12 }}
              >
                <label>⚠ Alergia / condición especial</label>
                <textarea
                  value={editor.condicionCritica}
                  onChange={(event) =>
                    setEditor((current) => ({
                      ...current,
                      condicionCritica: event.target.value,
                    }))
                  }
                  placeholder="Ej.: alergia severa a frutos secos"
                />
              </div>
            </div>

            <div className="mes-editor-footer">
              <div className="mes-editor-total">
                <small>Total producto</small>
                <strong>{money(editorTotal)}</strong>
              </div>

              <button
                type="button"
                className="mes-save"
                onClick={saveEditor}
                disabled={loadingAction}
              >
                {loadingAction
                  ? "Guardando…"
                  : editor.mode === "edit"
                    ? "Guardar cambios"
                    : "Agregar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {notice && (
        <div className={`mes-notice ${notice.type}`}>
          {notice.message}
        </div>
      )}
    </div>
  );
}
